/* ==========================================================================
   ToggleRxjs > Client > Prototype
   ========================================================================== */

/* Modules */
const { Store } = require('@bluekiri/alexandria/helpers/state');

const { fromEvent } = require('rxjs');
const { filter } = require('rxjs/operators');

const { types, actions } = require('./store/actions');
const { reducer } = require('./store/reducer');

const { initLoader, destroyLoader } = require('./loader');

const configuration = require('./config');

/* Prototype */
const prototype = {
    /* Pairing label */
    setPairing() {
        if (this.hooks.labels) {
            this.hooks.input.setAttribute('id', this.id);
            this.hooks.labels.forEach(label => label.setAttribute('for', this.id));
        }

        return this;
    },
    /* Setup store */
    setupStore() {
        if (this.store) return this;

        this.store = new Store({
            toggle: reducer
        });

        return this;
    },
    /* Setup actions */
    setupActions() {
        if (this.actions) return this;

        this.actions = {
            destroy: () => this.store.dispatch(actions.destroy()),
            display: () => this.store.dispatch(actions.display()),
            hide: () => this.store.dispatch(actions.hide()),
            toggle: () => this.store.dispatch(actions.toggle()),
            disable: () => this.store.dispatch(actions.disable()),
            enable: () => this.store.dispatch(actions.enable()),
            set: () => this.store.dispatch(actions.set()),
            unset: () => this.store.dispatch(actions.unset())
        };

        return this;
    },
    /* Setup reactions */
    setupReactions() {
        if (this.reactions) return this;

        this.reactions = {
            destroy: this.store.subscribe((state, action) => {
                if (action.type !== types.DESTROY) return;

                /* Loader */
                destroyLoader(this);

                /* Unsubscribe */
                this.unsubscribe();

                /* Remove from DOM */
                this.element.parentNode.removeChild(this.element);

                /* Clean reactions */
                [...Object.keys(this.reactions)].forEach(key => this.reactions[key].unsubscribe());

                /* Clean instance */
                [...Object.keys(this)].forEach(key => delete this[key]);
            }),
            display: this.store.subscribe((state, action) => {
                if (action.type !== types.DISPLAY) return;

                this.element.classList.remove(configuration().css.hidden);
            }),
            hide: this.store.subscribe((state, action) => {
                if (action.type !== types.HIDE) return;

                this.element.classList.add(configuration().css.hidden);
            }),
            disable: this.store.subscribe((state, action) => {
                if (action.type !== types.DISABLE) return;

                this.hooks.input.setAttribute('disabled', true);
                this.element.classList.add(configuration().css.disabled);
            }),
            enable: this.store.subscribe((state, action) => {
                if (action.type !== types.ENABLE) return;

                this.hooks.input.removeAttribute('disabled');
                this.element.classList.remove(configuration().css.disabled);
            }),
            set: this.store.subscribe((state, action) => {
                if (action.type !== types.SET) return;

                this.hooks.input.checked = true;
            }),
            unset: this.store.subscribe((state, action) => {
                if (action.type !== types.UNSET) return;

                this.hooks.input.checked = false;
            })
        };

        return this;
    },
    /* Subscribe */
    subscribe() {
        if (this.subscriptions) this.unsubscribe();

        this.subscriptions = {
            changes: fromEvent(this.element, 'change')
                .pipe(
                    filter(() => {
                        /* Data */
                        const { disabled } = this.store.select('toggle');

                        if (disabled) {
                            return false;
                        }

                        return true;
                    })
                )
                .subscribe(() => {
                    /* Dispatch action */
                    this.actions.toggle();
                })
        };

        return this;
    },
    /* Unsubscribe */
    unsubscribe() {
        if (!this.subscriptions) return this;

        [...Object.keys(this.subscriptions)].forEach(key => this.subscriptions[key].unsubscribe());

        return this;
    },
    /* Start */
    start() {
        if (!this.config.start) return this;

        /* Sync checked status */
        const { checked } = this.hooks.input;

        if (checked) {
            this.actions.toggle();
        }

        return this;
    },
    /* Init */
    init() {
        if (!this.config.init) return this;

        /* Set pairing */
        this.setPairing();

        /* Setup store */
        this.setupStore();

        /* Setup actions */
        this.setupActions();

        /* Setup reactions */
        this.setupReactions();

        /* Subscribe */
        this.subscribe();

        /* Loader */
        initLoader(this);

        /* Start */
        this.start();

        return this;
    }
};

/* Exports */
module.exports = prototype;
